import type {
  AdminPayment,
  AdminPaymentHistory,
  AdminPaymentKind,
} from "@/types/admin-payments";

/**
 * W65-3 — shape-check `GET /admin/users/{user_id}/payments` before support reads it.
 *
 * Fails closed as a WHOLE: a body or a single row that is not the expected shape
 * gives "unavailable", never an empty or partial list — either would tell support
 * the member paid less than they did (the gateway fails closed the same way).
 * An unknown `kind` is kept as "other" so a real payment is never dropped, and a
 * receipt link is kept only on Stripe's receipt host.
 */
const RECEIPT_PREFIX = "https://pay.stripe.com/";

const KINDS: ReadonlySet<string> = new Set<AdminPaymentKind>([
  "membership",
  "prepaid_membership",
  "gift",
  "replacement",
  "damage_fee",
  "other",
]);

export const PAYMENTS_UNAVAILABLE: AdminPaymentHistory = {
  available: false,
  payments: [],
  reason: "payments_unavailable",
};

function toPayment(raw: unknown): AdminPayment | null {
  if (!raw || typeof raw !== "object") return null;
  const p = raw as Record<string, unknown>;
  if (
    typeof p.id !== "string" ||
    typeof p.paidAt !== "string" ||
    typeof p.amountCents !== "number" ||
    typeof p.currency !== "string" ||
    typeof p.refundedCents !== "number" ||
    typeof p.kind !== "string"
  ) {
    return null;
  }
  const receipt = p.receiptUrl;
  return {
    id: p.id,
    paidAt: p.paidAt,
    amountCents: p.amountCents,
    currency: p.currency,
    refundedCents: p.refundedCents,
    kind: (KINDS.has(p.kind) ? p.kind : "other") as AdminPaymentKind,
    receiptUrl: typeof receipt === "string" && receipt.startsWith(RECEIPT_PREFIX) ? receipt : null,
  };
}

export function normalizePaymentHistory(raw: unknown): AdminPaymentHistory {
  if (!raw || typeof raw !== "object") return PAYMENTS_UNAVAILABLE;
  const body = raw as Record<string, unknown>;
  if (body.available !== true || !Array.isArray(body.payments)) return PAYMENTS_UNAVAILABLE;
  const payments: AdminPayment[] = [];
  for (const row of body.payments) {
    const payment = toPayment(row);
    if (payment === null) return PAYMENTS_UNAVAILABLE;
    payments.push(payment);
  }
  return { available: true, payments, reason: null };
}
